/* eslint-disable react-hooks/exhaustive-deps */
import axios from 'axios'
import React, {useEffect, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useLocation, useNavigate} from 'react-router-dom'
import * as auth from '../redux/AuthRedux'
import PulseLoader from 'react-spinners/PulseLoader'

interface TwoFactorInputProps {
  length?: number
  onComplete?: (code: string) => void
}

const TwoFactorInput: React.FC<TwoFactorInputProps> = ({length = 6, onComplete}) => {
  const [values, setValues] = useState<string[]>(Array(length).fill(''))
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const val = e.target.value
    if (!/^\d*$/.test(val)) return // only allow numbers

    const newValues = [...values]
    newValues[idx] = val.slice(-1) // keep last digit only
    setValues(newValues)

    if (val && idx < length - 1) {
      inputsRef.current[idx + 1]?.focus()
    }

    // trigger onComplete if all digits are filled
    if (newValues.every((v) => v !== '')) {
      onComplete?.(newValues.join(''))
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === 'Backspace') {
      if (values[idx]) {
        // clear current input
        const newValues = [...values]
        newValues[idx] = ''
        setValues(newValues)
      } else if (idx > 0) {
        // move to previous input
        inputsRef.current[idx - 1]?.focus()
      }
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData('text').trim().slice(0, length)
    if (!/^\d+$/.test(paste)) return

    const pasteValues = paste.split('')
    const newValues = [...values]
    for (let i = 0; i < pasteValues.length; i++) {
      newValues[i] = pasteValues[i]
      inputsRef.current[i]?.focus()
    }
    setValues(newValues)

    if (newValues.every((v) => v !== '')) {
      onComplete?.(newValues.join(''))
    }

    e.preventDefault()
  }

  return (
    <div className='d-flex flex-column justify-content-center mt-4 mb-2'>
      <label className='form-label fs-5 mb-1'>Enter Code</label>
      <div className='d-flex gap-2'>
        {values.map((value, idx) => (
          <input
            key={idx}
            type='text'
            inputMode='numeric'
            maxLength={1}
            className='form-control text-center'
            style={{width: '3rem', fontSize: '1.2rem', border: '1px solid #737373'}}
            value={value}
            onChange={(e) => handleChange(e, idx)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            onPaste={handlePaste}
            ref={(el) => (inputsRef.current[idx] = el)}
          />
        ))}
      </div>
    </div>
  )
}

const TwoFactorAuth: React.FC = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [isVerifying, setIsVerifying] = useState(false)
  const [errorMessage, setErrorMassage] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    if (!location?.state?.email) {
      navigate('/login')
    }
  }, [])

  const handleComplete = async (code: string) => {
    try {
      setIsVerifying(true)
      if (errorMessage) {
        setErrorMassage('')
      }

      const data = {
        email: location?.state?.email,
        token: parseInt(code),
      }
      const resp = await axios
        .post(`${process.env.REACT_APP_API_URL}/verify-2fa-token`, data)
        .then((response) => response.data)

      if (resp?.status === 200) {
        if (errorMessage) {
          setErrorMassage('')
        }

       setTimeout(() => {
         dispatch(auth.actions.login(resp?.data?.token))

         setTimeout(() => {
           navigate('/')
         }, 300)
       }, 500)

      } else {
        setErrorMassage(resp.message)
      }
    } catch (error) {
      setTimeout(() => {
        setErrorMassage('Invalid Code!')
      }, 500)
      console.log(error)
    } finally {
      setTimeout(() => {
        setIsVerifying(false)
      }, 500)
    }
  }

  return (
    <div className='d-flex flex-column justify-content-center align-items-center'>
      <h2 className='mb-4'>2-Step Verification</h2>

      {!location?.state?.isTwoFactorVerified && location?.state?.qrCode && (
        <img
          src={location?.state?.qrCode}
          alt='QR Code'
          height='150px'
          width='150px'
          className='border border-2 m-2 rounded-4'
        />
      )}

      <em className='mx-6 mt-2 text-center' style={{
        color: '#1b4256'
      }}>Enter the 6-digit code from your Google Authenticator app</em>

      <TwoFactorInput onComplete={handleComplete} />
      
      {errorMessage && (
        <div className='w-100 text-center fs-6 alert alert-danger mb-0 mt-4'>
          <div className='alert-text font-weight-bold'>{errorMessage}</div>
        </div>
      )}

      {isVerifying && (
        <div
          className='w-100 text-center fs-6 alert alert-success mb-0 mt-4 text-white'
          style={{
            backgroundColor: '#1b4256',
          }}
        >
          <div className='alert-text font-weight-bold'>
            <div className='d-flex justify-content-center align-items-center'>
              Verifying Code
              <span className='mt-1'>
                <PulseLoader color='#ffffff' size={'4px'} loading={isVerifying} />
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TwoFactorAuth
